import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function MethodologyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="text-2xl font-bold text-green-600">🍬</div>
              <h1 className="text-2xl font-bold text-gray-900">Gummy Truth UK</h1>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-gray-900">
                Home
              </Link>
              <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-gray-900">
                Blog
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Our Methodology</h1>
        
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Calculate Costs</h2>
            <Card>
              <CardContent className="pt-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  All cost calculations on GummyTruth UK are based on achieving the scientifically 
                  recommended daily dose of <strong>5g of creatine monohydrate</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This is the maintenance dose recommended by the International Society of Sports 
                  Nutrition and supported by decades of research.
                </p>
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sources</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span><strong>Pricing Data:</strong> Current UK retail prices from official brand websites 
                and major retailers (Amazon, MyProtein, Bulk.com) as of January 2025</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span><strong>Nutrition Information:</strong> Publicly available nutrition labels from 
                product packaging and official websites</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span><strong>Dosage Calculations:</strong> Based on label-reported creatine content 
                per serving, normalized to 5g daily dose</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Calculation Formula</h2>
            <Card className="bg-blue-50">
              <CardContent className="pt-6">
                <div className="font-mono text-sm space-y-2">
                  <p><strong>Daily Gummies Needed:</strong></p>
                  <p className="pl-4">= (Target Daily Dose / Creatine per Serving) × Gummies per Serving</p>
                  <p className="pl-4">= (5g / 3g) × 3 gummies = 5 gummies</p>
                  
                  <p className="mt-4"><strong>Days Per Pack:</strong></p>
                  <p className="pl-4">= Pack Size / Daily Gummies Needed</p>
                  <p className="pl-4">= 60 / 5 = 12 days</p>
                  
                  <p className="mt-4"><strong>Monthly Cost:</strong></p>
                  <p className="pl-4">= (30 days / Days Per Pack) × Pack Price</p>
                  <p className="pl-4">= (30 / 12) × £22.99 = £57.48</p>
                  
                  <p className="mt-4"><strong>Annual Cost:</strong></p>
                  <p className="pl-4">= Monthly Cost × 12</p>
                  <p className="pl-4">= £57.48 × 12 = £689.70</p>
                </div>
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Sugar Calculations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sugar content is calculated based on:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Label-reported sugar per gummy</li>
              <li>Daily gummies required to achieve 5g creatine dose</li>
              <li>365-day year for annual totals</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Example:</strong> MyProtein gummies contain 3.0g sugar per gummy. 
              Daily dose = 5 gummies. Daily sugar = 5 × 3.0g = 15g. Annual sugar = 15g × 365 = 5,475g = 5.48kg
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why 5g Per Day?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The 5g daily dose is the standard maintenance dose supported by research:
            </p>
            <Card>
              <CardContent className="pt-6">
                <ul className="space-y-2 text-gray-700">
                  <li>• Recommended by the International Society of Sports Nutrition</li>
                  <li>• Maintains elevated muscle creatine stores</li>
                  <li>• Provides consistent performance benefits</li>
                  <li>• Used in the majority of clinical studies</li>
                </ul>
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Price Updates</h2>
            <p className="text-gray-700 leading-relaxed">
              We monitor UK market prices regularly and update our calculator when significant 
              changes occur. Prices were last verified in <strong>January 2025</strong>. 
              All calculations use standard retail prices (not sale prices or promotional offers).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Transparency</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              GummyTruth UK operates transparently:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>All calculations are shown clearly to users</li>
              <li>We link to actual products so you can verify prices</li>
              <li>Our methodology is public and open to scrutiny</li>
              <li>We disclose affiliate relationships in our footer</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              If you believe any of our calculations contain errors or if you have questions 
              about our methodology, please contact us at:{' '}
              <a href="mailto:Gummytruth.uk@gmail.com" className="text-blue-600 hover:underline font-medium">
                Gummytruth.uk@gmail.com
              </a>
            </p>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500">
              Last Updated: January 26, 2025
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-900">
              <strong>Gummy Truth UK © 2025</strong>
            </p>
            <div className="flex justify-center space-x-6 text-sm">
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>
              <Link to="/blog" className="text-blue-600 hover:underline">
                Blog
              </Link>
              <Link to="/" className="text-blue-600 hover:underline">
                Calculator
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
